import { useMemo } from 'react';
import { where } from 'firebase/firestore';
import { canOrg, type Project } from '@allemnii-pulse/shared';
import { useAuth } from '../features/auth/AuthProvider.js';
import { useOrgs } from '../features/orgs/OrgProvider.js';
import { useCollection, type LiveCollectionState } from './firestore.js';

/**
 * Live list of the current org's projects that the signed-in user can see: every project for an
 * org owner/admin (org.projects.viewAll — they implicitly hold a role on all of them), otherwise
 * just the ones they're a member of.
 *
 * The query shape matters, not just the result: Firestore only allows a list query whose every
 * possible match is provably readable from the query's own filters. The admin branch of the
 * projects read rule is provable from `orgId ==` alone; the member branch checks memberUids, so a
 * non-admin's query must carry the `memberUids array-contains` filter or it's denied outright.
 */
export function useOrgProjects(): LiveCollectionState<Project> {
  const { currentOrgId, currentOrg } = useOrgs();
  const { user } = useAuth();
  const seeAll = canOrg(currentOrg?.role, 'org.projects.viewAll');

  const constraints = useMemo(() => {
    if (!currentOrgId || !user) return [];
    const byOrg = where('orgId', '==', currentOrgId);
    return seeAll ? [byOrg] : [byOrg, where('memberUids', 'array-contains', user.uid)];
  }, [currentOrgId, user, seeAll]);

  return useCollection<Project>(currentOrgId && user ? 'projects' : null, constraints);
}
